<template>
  <section class="bg-sectionwize covidproof section-30 generic">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-12">
          <div class="section-ptb text-center">
            <h2 class="trending-title mb-4">Trending Franchises</h2>
            <div class="toggle-group-container mb-4">
              <div class="toggle-group" role="group">
                <button 
                  type="button" 
                  class="toggle-btn" 
                  :class="{ active: activeTab === 'week' }" 
                  @click="activeTab = 'week'"
                >
                  <i class="fa-solid fa-calendar-week"></i>
                  This Week
                </button>
                <button 
                  type="button" 
                  class="toggle-btn" 
                  :class="{ active: activeTab === 'month' }" 
                  @click="activeTab = 'month'"
                >
                  <i class="fa-solid fa-calendar-days"></i>
                  This Month
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-md-12">
          <div v-if="franchiseStore.loading" class="text-center">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div v-else-if="franchiseStore.error" class="text-center text-danger">
            {{ franchiseStore.error }}
          </div>
          <div v-else-if="mappedFranchises.length === 0" class="text-center">
            <p>No trending franchises found.</p>
          </div>
          <div v-else class="trending-franchise-wrap d-flex flex-wrap justify-content-center">
            <CardSection 
              v-for="franchise in mappedFranchises" 
              :key="franchise.id" 
              :franchise="franchise" 
              :show-lead-modal="true"
              class="m-2" 
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useFranchiseStore } from '@/stores/franchiseStore'
import { mapFranchiseData } from '@/utils/formatters'
import CardSection from './CardSection.vue'

const franchiseStore = useFranchiseStore()
const activeTab = ref('week')

onMounted(async () => {
  await franchiseStore.fetchFranchises()
})

const filteredFranchises = computed(() => {
  // Ensure franchises array exists and is not empty
  if (!franchiseStore.franchises || franchiseStore.franchises.length === 0) {
    return []
  }

  if (activeTab.value === 'week') {
    return franchiseStore.trendingFranchises || []
  }
  if (activeTab.value === 'month') {
    return franchiseStore.monthlyFranchises || []
  }
  return franchiseStore.franchises || []
})

const mappedFranchises = computed(() => {
  // Ensure filteredFranchises is an array before mapping
  if (!filteredFranchises.value || !Array.isArray(filteredFranchises.value)) {
    return []
  }
  return filteredFranchises.value.map(franchise => mapFranchiseData(franchise))
})
</script>

<style scoped>
.trending-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #000000;
  margin-bottom: 1rem;
  letter-spacing: -0.5px;
}

.toggle-group-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.toggle-group {
  display: inline-flex;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(44,62,80,0.08);
  border: 1px solid #e8eaed;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}

.toggle-btn i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.toggle-btn:hover {
  color: var(--primary-orange, #ff6b35);
  background: rgba(255,107,53,0.05);
  transform: translateY(-1px);
}

.toggle-btn:hover i {
  transform: scale(1.1);
}

.toggle-btn.active {
  background: var(--primary-orange, #ff6b35);
  color: #fff;
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
  transform: translateY(-1px);
}

.toggle-btn.active i {
  transform: scale(1.1);
}

.trending-franchise-wrap {
  gap: 1.5rem;
  margin-top: 2rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
}

@media (max-width: 900px) {
  .trending-title {
    font-size: 2.2rem;
  }
  
  .toggle-group {
    padding: 4px;
  }
  
  .toggle-btn {
    padding: 10px 18px;
    font-size: 0.95rem;
  }
  
  .trending-franchise-wrap {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 16px;
    padding-bottom: 8px;
    scrollbar-width: none;
  }
  
  .trending-franchise-wrap::-webkit-scrollbar {
    display: none;
  }
  
  .trending-franchise-wrap > * {
    min-width: 300px !important;
    max-width: 90vw !important;
    flex: 0 0 auto !important;
    width: 300px !important;
  }
}

@media (max-width: 1200px) {
  .trending-franchise-wrap > * {
    width: calc(33.333% - 1rem); /* 3 cards per row */
  }
}

@media (max-width: 900px) {
  .trending-franchise-wrap > * {
    width: calc(50% - 0.75rem); /* 2 cards per row */
  }
}

@media (max-width: 600px) {
  .trending-title {
    font-size: 1.8rem;
  }
  
  .toggle-group {
    flex-direction: column;
    gap: 8px;
    padding: 8px;
  }
  
  .toggle-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 20px;
  }
  
  .trending-franchise-wrap > * {
    width: 100%; /* 1 card per row on mobile */
    max-width: 100%;
  }
}
</style>




<!-- <style scoped>
.trending-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #000000;
  margin-bottom: 1rem;
  letter-spacing: -0.5px;
}

.toggle-group-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.toggle-group {
  display: inline-flex;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 2px 8px rgba(44,62,80,0.08);
  border: 1px solid #e8eaed;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}

.toggle-btn i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.toggle-btn:hover {
  color: var(--primary-orange, #ff6b35);
  background: rgba(255,107,53,0.05);
  transform: translateY(-1px);
}

.toggle-btn:hover i {
  transform: scale(1.1);
}

.toggle-btn.active {
  background: var(--primary-orange, #ff6b35);
  color: #fff;
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
  transform: translateY(-1px);
}

.toggle-btn.active i {
  transform: scale(1.1);
}

.trending-franchise-wrap {
  gap: 1.5rem;
  margin-top: 2rem;
}

@media (max-width: 900px) {
  .trending-title {
    font-size: 2.2rem;
  }
  
  .toggle-group {
    padding: 4px;
  }
  
  .toggle-btn {
    padding: 10px 18px;
    font-size: 0.95rem;
  }
  
  .trending-franchise-wrap {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 16px;
    padding-bottom: 8px;
    scrollbar-width: none;
  }
  
  .trending-franchise-wrap::-webkit-scrollbar {
    display: none;
  }
  
  .trending-franchise-wrap > * {
    min-width: 300px;
    max-width: 90vw;
    flex: 0 0 auto;
  }
}

@media (max-width: 600px) {
  .trending-title {
    font-size: 1.8rem;
  }
  
  .toggle-group {
    flex-direction: column;
    gap: 8px;
    padding: 8px;
  }
  
  .toggle-btn {
    width: 100%;
    justify-content: center;
    padding: 12px 20px;
  }
}
</style>  -->